import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import ClassSelect from '../components/ClassSelect';
import ConfirmationDialog from '../components/ConfirmationDialog';
import Loading from '../components/Loading';
import CategoriesReorderDialog from '../components/RatingSentencesEditor/CategoriesReorderDialog';
import CategoryEditDialog, {
  CategoryWithYearSegments,
} from '../components/RatingSentencesEditor/CategoryEditDialog';
import CategoryList from '../components/RatingSentencesEditor/CategoryList';
import RatingSentenceEditDialog from '../components/RatingSentencesEditor/RatingSentenceEditDialog';
import SubjectSelector from '../components/SubjectSelector';
import categoriesOrderService from '../services/categories-order-service';
import categoriesService from '../services/categories-service';
import ratingsService from '../services/ratings-service';
import subjectsService from '../services/subjects-service';
import { CategoriesOrder } from '../types/categories-order';
import { CategoriesWithRatingsByYearSegment } from '../types/categories-with-ratings-by-year-segment';
import { Category, YearSegment } from '../types/category';
import { RatingSentence } from '../types/rating-sentence';
import { Subject } from '../types/subject';

const RatingSentencesEdit: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [showFeminine, setShowFeminine] = useState(false);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedYear, setSelectedYear] = useState(1);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<
    CategoryWithYearSegments | null | 'create'
  >(null);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
    null
  );
  const [ratingToEdit, setRatingToEdit] = useState<
    RatingSentence | null | 'create'
  >(null);
  const [ratingToDelete, setRatingToDelete] = useState<RatingSentence | null>(
    null
  );
  const [
    categoriesWithRatingsByYearSegment,
    setCategoriesWithRatingsByYearSegment,
  ] = useState<CategoriesWithRatingsByYearSegment>(
    new CategoriesWithRatingsByYearSegment()
  );

  const groupCategoriesWithRatings = useCallback(
    async (
      categoriesOrder: CategoriesOrder
    ): Promise<CategoriesWithRatingsByYearSegment> => {
      const allCategoryIds = [
        ...categoriesOrder.categoryIdsWholeYear,
        ...categoriesOrder.categoryIdsFirstHalf,
        ...categoriesOrder.categoryIdsSecondHalf,
      ];
      return Promise.all([
        categoriesService.getMany(allCategoryIds),
        ratingsService.getByCategoryIds(allCategoryIds),
      ]).then(
        ([categories, ratings]) =>
          new CategoriesWithRatingsByYearSegment(
            categories,
            categoriesOrder,
            ratings
          )
      );
    },
    []
  );

  const fetchCategoriesWithRatings = () => {
    setLoadingCategories(true);

    categoriesOrderService
      .getBySubjectIdAndYear(selectedSubjectId, selectedYear)
      .then((categoriesOrder) =>
        !categoriesOrder
          ? new CategoriesWithRatingsByYearSegment()
          : groupCategoriesWithRatings(categoriesOrder)
      )
      .then(setCategoriesWithRatingsByYearSegment)
      .finally(() => setLoadingCategories(false));
  };

  useEffect(fetchCategoriesWithRatings, [
    selectedYear,
    selectedSubjectId,
    groupCategoriesWithRatings,
  ]);

  useEffect(() => {
    subjectsService
      .getAll()
      .then(setSubjects)
      .finally(() => setLoading(false));
  }, []);

  const areCategoriesEqual = (category: Category, other: Category) =>
    other.id !== category.id && other.name === category.name;

  const isCategoryNotUnique = (category: Category) =>
    categoriesWithRatingsByYearSegment.wholeYear.some((cr) =>
      areCategoriesEqual(category, cr.category)
    ) ||
    categoriesWithRatingsByYearSegment.firstHalf.some((cr) =>
      areCategoriesEqual(category, cr.category)
    ) ||
    categoriesWithRatingsByYearSegment.secondHalf.some((cr) =>
      areCategoriesEqual(category, cr.category)
    );

  const isRatingNotUnique = (rating: RatingSentence) =>
    categoriesWithRatingsByYearSegment.wholeYear
      .find((c) => c.category.id === rating.categoryId)
      ?.ratings.some((r) => r.id !== rating.id && r.text === rating.text) ||
    categoriesWithRatingsByYearSegment.firstHalf
      .find((c) => c.category.id === rating.categoryId)
      ?.ratings.some((r) => r.id !== rating.id && r.text === rating.text) ||
    categoriesWithRatingsByYearSegment.secondHalf
      .find((c) => c.category.id === rating.categoryId)
      ?.ratings.some((r) => r.id !== rating.id && r.text === rating.text);

  const onCategoryEdit = (category: Category) => {
    getYearSegments(category)
      .then((yearSegments) => setCategoryToEdit({ category, yearSegments }))
      .catch((err) => {
        console.error(err);
        alert('Chyba při upravování kategorie');
      });
  };

  const handleSetYear = (newClassYear: number) => setSelectedYear(newClassYear);

  const getCategoryNotUniqueText = (category: Category) =>
    `Kategorie s názvem ${category.name} již${
      selectedSubjectId === '' ? '' : ' pro tento předmět v tomto ročníku'
    } existuje`;

  const handleCreateCategory = async (
    newCategory: Category,
    yearSegments: Record<number, YearSegment>
  ) => {
    if (isCategoryNotUnique(newCategory)) {
      alert(getCategoryNotUniqueText(newCategory));
      return;
    }

    await categoriesService.add(newCategory, yearSegments);
    fetchCategoriesWithRatings();
    setCategoryToEdit(null);
  };

  const handleEditCategory = async (
    updatedCategory: Category,
    yearSegments: Record<number, YearSegment>
  ) => {
    if (isCategoryNotUnique(updatedCategory)) {
      alert(getCategoryNotUniqueText(updatedCategory));
      return;
    }

    await categoriesService.update(updatedCategory, yearSegments);
    fetchCategoriesWithRatings();
    setCategoryToEdit(null);
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete?.id) {
      throw new Error('Cannot delete a category without an ID');
    }

    const ratingsToDelete = await ratingsService.getByCategoryIds([
      categoryToDelete.id.toString(),
    ]);

    if (ratingsToDelete.some((rating) => rating.id === undefined)) {
      throw new Error(
        'Cannot delete a category that contains a rating without an ID'
      );
    }

    await ratingsService.removeBatch(
      ratingsToDelete.map((rating) => rating.id!)
    );
    await categoriesService.remove(categoryToDelete);

    fetchCategoriesWithRatings();
    setCategoryToDelete(null);
  };

  const handleCreateRating = async (newRating: RatingSentence) => {
    if (isRatingNotUnique(newRating)) {
      alert('Hodnotící věta s tímto textem již pro tuto kategorii existuje');
      return;
    }
    await ratingsService.add(newRating);
    setRatingToEdit(null);
    fetchCategoriesWithRatings();
  };

  const handleEditRating = async (updatedRating: RatingSentence) => {
    if (isRatingNotUnique(updatedRating)) {
      alert('Hodnotící věta s tímto textem již pro tuto kategorii existuje');
      return;
    }
    await ratingsService.update(updatedRating);
    setRatingToEdit(null);
    fetchCategoriesWithRatings();
  };

  const handleDeleteRating = async () => {
    if (!ratingToDelete?.id) {
      throw new Error('Cannot delete a rating without an ID');
    }

    await ratingsService.remove(ratingToDelete.id);
    setRatingToDelete(null);
    fetchCategoriesWithRatings();
  };

  const handleReorderCategories = async (newOrder: CategoriesOrder) => {
    setLoadingCategories(true);

    const flattenedCategoryIds = [
      ...newOrder.categoryIdsWholeYear,
      ...newOrder.categoryIdsFirstHalf,
      ...newOrder.categoryIdsSecondHalf,
    ];

    try {
      for (const categoryId of flattenedCategoryIds) {
        if (flattenedCategoryIds.filter((id) => id === categoryId).length > 1) {
          throw new Error(
            `Category id ${categoryId} is duplicated in the new order`
          );
        }
        if (!(await categoriesService.doesCategoryExist(categoryId))) {
          throw new Error(`Category with id ${categoryId} does not exist`);
        }
      }

      await categoriesOrderService.updateBySubjectIdAndYear(
        selectedSubjectId,
        selectedYear,
        newOrder
      );

      setCategoriesWithRatingsByYearSegment(
        getReorderedCategoriesByNewOrder(newOrder)
      );
    } catch (err) {
      console.error('Error while reordering categories', err);
      alert('Chyba při úpravě pořadí kategorií');
      fetchCategoriesWithRatings();
      return;
    } finally {
      setLoadingCategories(false);
      setIsEditingOrder(false);
    }
  };

  const getYearSegments = async (
    category: Category
  ): Promise<Record<number, YearSegment> | undefined> => {
    const subjectId = category.subjectId;
    if (!subjectId || subjectId === '') return undefined;

    const orders = await categoriesOrderService.getBySubjectId(subjectId);
    const yearSegments: Record<number, YearSegment> = {};
    orders.forEach((order, index) => {
      const year = index + 1;
      if (order.categoryIdsWholeYear.includes(category.id!.toString())) {
        yearSegments[year] = YearSegment.WholeYear;
      } else if (order.categoryIdsFirstHalf.includes(category.id!.toString())) {
        yearSegments[year] = YearSegment.FirstHalf;
      } else if (
        order.categoryIdsSecondHalf.includes(category.id!.toString())
      ) {
        yearSegments[year] = YearSegment.SecondHalf;
      }
    });

    return yearSegments;
  };

  const getReorderedCategoriesByNewOrder = (newOrder: CategoriesOrder) => {
    const categories = categoriesWithRatingsByYearSegment.flattenCategories();
    const newCategoriesWithRatings = new CategoriesWithRatingsByYearSegment();

    newOrder.categoryIdsWholeYear.forEach((id) => {
      const category = categories.find((cr) => cr.category.id === id);
      if (category) {
        newCategoriesWithRatings.wholeYear.push(category);
      } else {
        console.error(`Category with id ${id} not found`);
      }
    });

    if (selectedSubjectId === '') return newCategoriesWithRatings;

    newOrder.categoryIdsFirstHalf.forEach((id) => {
      const category = categories.find((cr) => cr.category.id === id);
      if (category) {
        newCategoriesWithRatings.firstHalf.push(category);
      } else {
        console.error(`Category with id ${id} not found`);
      }
    });

    newOrder.categoryIdsSecondHalf.forEach((id) => {
      const category = categories.find((cr) => cr.category.id === id);
      if (category) {
        newCategoriesWithRatings.secondHalf.push(category);
      } else {
        console.error(`Category with id ${id} not found`);
      }
    });

    return newCategoriesWithRatings;
  };

  const getNoCategoriesText = (id: string): string => {
    const subjectName = subjects.find((subject) => subject.id === id)?.name;

    if (selectedSubjectId === '') {
      return 'Žádné univerzální hodnotící kategorie';
    } else if (subjectName) {
      return `Žádné hodnotící kategorie pro ${selectedYear}. třídu v předmětu ${subjectName}`;
    } else {
      return 'Žádné hodnocení';
    }
  };

  const renderCategoryEditDialog = () => (
    <CategoryEditDialog
      onClose={() => setCategoryToEdit(null)}
      onConfirm={
        categoryToEdit === 'create' ? handleCreateCategory : handleEditCategory
      }
      categoryWithYearSegments={
        categoryToEdit === 'create' ? null : categoryToEdit
      }
      selectedSubject={
        selectedSubjectId === ''
          ? null
          : subjects.find((subject) => subject.id === selectedSubjectId) ?? null
      }
    />
  );

  const renderCategoryDeleteDialog = () => (
    <ConfirmationDialog
      onClose={() => setCategoryToDelete(null)}
      onConfirm={handleDeleteCategory}
      title={`Smazat kategorii ${categoryToDelete!.name}?`}
      description={getCategoryDeletionDescription(categoryToDelete!)}
      confirmText="Smazat"
    />
  );

  const getCategoryDeletionDescription = (category: Category) => {
    const ratings =
      categoriesWithRatingsByYearSegment.wholeYear.find(
        (c) => c.category.id === category.id
      )?.ratings ??
      categoriesWithRatingsByYearSegment.firstHalf.find(
        (c) => c.category.id === category.id
      )?.ratings ??
      categoriesWithRatingsByYearSegment.secondHalf.find(
        (c) => c.category.id === category.id
      )?.ratings ??
      [];

    switch (ratings.length) {
      case 0:
        return '';
      case 1:
        return 'Smazáním této kategorie se smaže i jedna hodnotící věta kterou obsahuje.';
      case 2:
      case 3:
      case 4:
        return `Smazáním této kategorie se smažou i ${ratings.length} hodnotící věty které obsahuje.`;
      default:
        return `Smazáním této kategorie se smaže i ${ratings.length} hodnotících vět které obsahuje.`;
    }
  };

  const renderRatingEditDialog = () => (
    <RatingSentenceEditDialog
      onClose={() => setRatingToEdit(null)}
      subjectName={
        subjects.find((subject) => subject.id === selectedSubjectId)?.name ?? ''
      }
      onConfirm={
        ratingToEdit === 'create' ? handleCreateRating : handleEditRating
      }
      rating={ratingToEdit === 'create' ? null : ratingToEdit}
      categories={categoriesWithRatingsByYearSegment
        .flattenCategories()
        .map((c) => c.category)}
    />
  );

  const renderRatingDeleteDialog = () => (
    <ConfirmationDialog
      onClose={() => setRatingToDelete(null)}
      onConfirm={handleDeleteRating}
      title={'Smazat hodnocení?'}
      description={
        showFeminine
          ? ratingToDelete!.feminine ?? ratingToDelete!.text
          : ratingToDelete!.text
      }
      confirmText="Smazat"
    />
  );

  const renderCategoriesReorderDialog = () => (
    <CategoriesReorderDialog
      onClose={() => setIsEditingOrder(false)}
      onConfirm={handleReorderCategories}
      categoriesWithRatings={categoriesWithRatingsByYearSegment}
    />
  );

  const renderDialog = () => {
    if (categoryToDelete) {
      return renderCategoryDeleteDialog();
    } else if (categoryToEdit !== null) {
      return renderCategoryEditDialog();
    } else if (ratingToDelete) {
      return renderRatingDeleteDialog();
    } else if (ratingToEdit !== null) {
      return renderRatingEditDialog();
    } else if (isEditingOrder) {
      return renderCategoriesReorderDialog();
    }
  };

  return loading ? (
    <Loading />
  ) : (
    <>
      {renderDialog()}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h4">Úprava hodnotících vět</Typography>
        {selectedSubjectId !== '' && (
          <ClassSelect
            outlined
            onChange={handleSetYear}
            defaultValue={selectedYear}
          />
        )}
      </Box>
      <SubjectSelector
        subjects={subjects}
        selectedSubjectId={selectedSubjectId}
        onSelectedSubjectIdChange={setSelectedSubjectId}
        showUniversal
      />
      <Box display="flex" justifyContent={'start'} gap={1}>
        <Button
          variant="contained"
          onClick={() => setCategoryToEdit('create')}
          disabled={loadingCategories}
        >
          Přidat kategorii
        </Button>
        <Button
          variant="contained"
          onClick={() => setRatingToEdit('create')}
          disabled={
            categoriesWithRatingsByYearSegment.flattenCategories().length ===
              0 || loadingCategories
          }
        >
          Přidat větu
        </Button>
        <Box width={2} />
        <RadioGroup row value={showFeminine}>
          <FormControlLabel
            value={false}
            control={<Radio />}
            label={<MaleIcon />}
            onChange={() => setShowFeminine(false)}
          />
          <FormControlLabel
            value={true}
            control={<Radio />}
            label={<FemaleIcon />}
            onChange={() => setShowFeminine(true)}
          />
        </RadioGroup>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          onClick={() => setIsEditingOrder(true)}
          disabled={
            loadingCategories ||
            categoriesWithRatingsByYearSegment.flattenCategories().length <= 1
          }
        >
          Upravit pořadí
        </Button>
      </Box>
      {loadingCategories ? (
        <Loading />
      ) : categoriesWithRatingsByYearSegment.flattenCategories().length ===
        0 ? (
        <Typography variant="h6" mt={3} noWrap>
          {getNoCategoriesText(selectedSubjectId)}
        </Typography>
      ) : (
        <CategoryList
          categoriesWithRatingsByYearSegment={
            categoriesWithRatingsByYearSegment
          }
          onCategoryEdit={onCategoryEdit}
          onCategoryDelete={setCategoryToDelete}
          onRatingEdit={setRatingToEdit}
          onRatingDelete={setRatingToDelete}
          showFeminine={showFeminine}
        />
      )}
    </>
  );
};

export default RatingSentencesEdit;
